import { AppError } from "../../../shared/utils/error-handler";
import type { CompanyOrderRepository } from "../../../domain/repositories/company/company-order-repository";
import type { GetOrdersResponse, CreateOrderRequest, CreateOrderResponse, Courier, OrderPageResponse, BulkShipmentRegisterResponse, OrderSearchSuggestion } from "../../types/orders";
import type { ApiResponse } from "../../../shared/types/common";
import type { GetResubmissionResponse } from "../../types/resubmission";

export class CompanyOrderUsecase {
    constructor(private companyOrderRepository: CompanyOrderRepository) {}

    async trackingOrder(companyId: number, orderId: number, trackingNumber: string, courier: string): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.trackingOrder(companyId, orderId, trackingNumber, courier);
        } catch (error) {
            throw new AppError(
                String(error) || "배송추적에 실패하였습니다.",
                "ORDER_GET_FAILED",
                error,
            );
        }
    }

    async refreshTracking(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.refreshTracking(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "배송 정보를 갱신하지 못했습니다.",
                "ORDER_TRACKING_REFRESH_FAILED",
                error,
            );
        }
    }

    getOrderSearchSuggestions(companyId: number, keyword: string): Promise<OrderSearchSuggestion[]> {
        return this.companyOrderRepository.getOrderSearchSuggestions(companyId, keyword);
    }

    async getOrders(companyId: number, status?: string, keyword?: string, page?: number, size?: number, dateFrom?: string, dateTo?: string): Promise<OrderPageResponse> {
        try {
            return await this.companyOrderRepository.getOrders(companyId, status, keyword, page, size, dateFrom, dateTo);
        } catch (error) {
            throw new AppError(
                String(error) || "주문 내역을 불러오지 못했습니다.",
                "ORDER_GET_FAILED",
                error,
            );
        }
    }

    async resubmission(companyId: number, orderId: number, memo: string): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.resubmission(companyId, orderId, memo);
        } catch (error) {
            throw new AppError(
                String(error) || "재접수 변경을 못했습니다.",
                "ORDER_GET_FAILED",
                error,
            );
        }
    }

    async resubmissionList(companyId: number): Promise<GetResubmissionResponse> {
        try {
            return await this.companyOrderRepository.resubmissionList(companyId);
        } catch (error) {
            throw new AppError(
                String(error) || "주문 내역을 불러오지 못했습니다.",
                "ORDER_GET_FAILED",
                error,
            );
        }
    }

    async acceptOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.acceptOrder(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "접수 변경을 못했습니다.",
                "ORDER_GET_FAILED",
                error,
            );
        }
    }

    async getCourierList(): Promise<Courier[]> {
        try {
            return await this.companyOrderRepository.getCourierList();
        } catch (error) {
            throw new AppError(
                String(error) || "택배사 목록을 가져오지 못했습니다.",
                "GET_COURIER_LIST_FAILED",
                error,
            );
        }
    }

    async updateTrackingInfo(companyId: number, orderId: number, courier: string, trackingNumber: string): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.updateTrackingInfo(companyId, orderId, courier, trackingNumber);
        } catch (error) {
            throw new AppError(
                String(error) || "배송 정보를 등록하지 못했습니다.",
                "GET_COURIER_LIST_FAILED",
                error,
            );
        }
    }

    async createOrder(companyId: number, orderData: CreateOrderRequest, orderSourceFile?: File): Promise<CreateOrderResponse> {
        try {
            return await this.companyOrderRepository.createOrder(companyId, orderData, orderSourceFile);
        } catch (error) {
            throw new AppError(
                String(error) || "주문 생성에 실패했습니다.",
                "ORDER_CREATE_FAILED",
                error,
            );
        }
    }

    async startProduction(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.startProduction(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "제작 시작에 실패했습니다.",
                "START_PRODUCTION_FAILED",
                error,
            );
        }
    }

    async completeProduction(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.completeProduction(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "제작 완료 처리에 실패했습니다.",
                "COMPLETE_PRODUCTION_FAILED",
                error,
            );
        }
    }

    async completeDelivery(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.completeDelivery(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "배송 완료 처리에 실패했습니다.",
                "COMPLETE_DELIVERY_FAILED",
                error,
            );
        }
    }

    async shipOrder(companyId: number, orderId: number, courier?: string, trackingNumber?: string): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.shipOrder(companyId, orderId, courier, trackingNumber);
        } catch (error) {
            throw new AppError(
                String(error) || "출고/배송 처리에 실패했습니다.",
                "SHIP_ORDER_FAILED",
                error,
            );
        }
    }

    async cancelOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.cancelOrder(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "주문 취소에 실패했습니다.",
                "CANCEL_ORDER_FAILED",
                error,
            );
        }
    }

    async refundOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.refundOrder(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "환불 처리에 실패했습니다.",
                "REFUND_ORDER_FAILED",
                error,
            );
        }
    }

    async resetToBeforePayment(companyId: number, orderId: number): Promise<ApiResponse> {
        try {
            return await this.companyOrderRepository.resetToBeforePayment(companyId, orderId);
        } catch (error) {
            throw new AppError(
                String(error) || "결제전 상태 변경에 실패했습니다.",
                "RESET_TO_BEFORE_PAYMENT_FAILED",
                error,
            );
        }
    }

    async bulkShipmentRegister(companyId: number, file: File): Promise<BulkShipmentRegisterResponse> {
        try {
            return await this.companyOrderRepository.bulkShipmentRegister(companyId, file);
        } catch (error) {
            throw new AppError(
                String(error) || "일괄 출고등록에 실패했습니다.",
                "BULK_SHIPMENT_REGISTER_FAILED",
                error,
            );
        }
    }

    async downloadShippingTemplate(companyId: number): Promise<Blob> {
        try {
            return await this.companyOrderRepository.downloadShippingTemplate(companyId);
        } catch (error) {
            throw new AppError(
                String(error) || "템플릿 다운로드에 실패했습니다.",
                "DOWNLOAD_SHIPPING_TEMPLATE_FAILED",
                error,
            );
        }
    }
}
